import { useAllMedicationRecords } from '@/hooks/useAllMedicationRecords';
import { groupMedicationRecords, type MedicationGroup } from '@liza/fhir-utils';
import { useMemo } from 'react';

/**
 * The medication group a given record belongs to — the record the list displays plus everything it
 * supersedes, newest first.
 *
 * The detail panel is addressed by resource id alone (the route carries `id` +
 * `medicationResourceType`), so it cannot know which other records collapsed into the row that was
 * clicked. Rather than plumb the group through the URL, this re-derives it with the same
 * `groupMedicationRecords` the list uses, so the two can't disagree about what belongs together.
 *
 * `group` is a single-record group for a medication that shares its RxNorm code with nothing else
 * (or carries no code at all, which never groups), and `undefined` until the lookup resolves or when
 * the id is not among the records it fetched.
 *
 * `isPending` is reported so the caller can tell "still loading" from "loaded, no match" — those want
 * different handling, and a caller that renders the group's records must not gate on an unrelated
 * query's state instead.
 */
export function useMedicationGroup(
  medplumPatientId: string,
  id: string | undefined,
): { group: MedicationGroup | undefined; isPending: boolean } {
  const enabled = Boolean(medplumPatientId && id);
  // `all`, not `active`: the list this feeds can be filtered to inactive, and a grouped inactive row
  // carries the same `Nx` badge as an active one. Looking up only active records would find nothing
  // for such a row and silently fall back to a single record — showing fewer than the badge promised,
  // with no sign anything was dropped. The hook cannot see the list's filter, so it declines to
  // depend on one.
  // `internal` scopes the search to `subject=Patient/<id>`. Without it the search falls back to
  // `_compartment`, which also matches the inactive shadow Patients that Metriport/Fasten imports
  // hang their records off — so a group could present an outside pharmacy's record as part of this
  // patient's history. Same scope the care-plan medication hooks use.
  const { records, isPending } = useAllMedicationRecords(
    medplumPatientId,
    { ownership: 'internal', status: 'all' },
    enabled,
  );

  const group = useMemo(() => {
    if (!id || isPending) return undefined;
    const groups = groupMedicationRecords(records);
    return groups.find(
      (candidate) => candidate.record.id === id || candidate.superseded.some((superseded) => superseded.id === id),
    );
  }, [id, isPending, records]);

  // `useAllMedicationRecords` already folds the `enabled` gate into `isPending`, so a lookup that
  // was never going to run reports done rather than hanging.
  return { group, isPending };
}
